import { neutral } from "theme/colors";

const BRAND = "primary.main";
const BRAND_DARK = "primary.dark";
// Brand alphas — RGB derived from theme.palette.primary.main (#DC4313 → 220, 67, 19).
// Update these RGB triplets if the brand color changes.
const BRAND_ALPHA_04 = "rgba(220, 67, 19, 0.04)";
const BRAND_ALPHA_06 = "rgba(220, 67, 19, 0.06)";
const BRAND_ALPHA_08 = "rgba(220, 67, 19, 0.08)";
const BRAND_ALPHA_10 = "rgba(220, 67, 19, 0.10)";
const BRAND_ALPHA_12 = "rgba(220, 67, 19, 0.12)";
const BRAND_ALPHA_35 = "rgba(220, 67, 19, 0.35)";
const TEXT_PRIMARY = "rgba(18, 24, 38, 0.92)";
// Bumped from 0.56 → 0.65: at 0.56 this muted ink resolves to ~4.1:1 on the
// white/#F3F4F6 surfaces, below WCAG 2.1 AA 1.4.3 (4.5:1) for the 12–13px
// secondary text (subtitles, file type, empty copy). 0.65 clears 4.5:1 on both.
const TEXT_SECONDARY = "rgba(18, 24, 38, 0.65)";
const BORDER = "rgba(18, 24, 38, 0.08)";
const SURFACE = "common.white";
const SURFACE_ALT = neutral[100];

const cloudStoragePickerStyles = {
  // ── Trigger button + menu ───────────────────────────────────────────
  triggerBtn: {
    textTransform: "none",
    fontSize: "14px",
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    minWidth: "200px",
  },
  menuItemIcon: {
    width: "18px",
    height: "18px",
    objectFit: "contain",
  },
  menuItemLabel: {
    flexGrow: 1,
    fontSize: "14px",
  },
  disabledLabel: {
    fontSize: "12px",
    color: "text.disabled",
    fontStyle: "italic",
  },

  // The dialog shell, header and footer bar are no longer defined here: the
  // picker renders inside `AppModal`, which owns the paper, scrim, header
  // (provider mark in the icon tile, title, subtitle, close) and the footer
  // bar. Only the picker's own body chrome remains below.

  // ── Toolbar ─────────────────────────────────────────────────────────
  toolbar: {
    display: "flex",
    flexDirection: "column",
    gap: 1,
    px: 3,
    pt: 2,
    pb: 1.25,
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      height: "42px",
      borderRadius: "10px",
      backgroundColor: SURFACE,
      fontSize: "14px",
      paddingLeft: "12px",
      paddingRight: "12px",
      transition: "border-color 140ms ease, box-shadow 140ms ease",
      "& fieldset": {
        borderColor: BORDER,
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "rgba(18, 24, 38, 0.2)",
      },
      "&.Mui-focused": {
        boxShadow: `0 0 0 3px ${BRAND_ALPHA_12}`,
        "& fieldset": {
          borderColor: BRAND,
          borderWidth: "1px",
        },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "10px 4px",
      "&::placeholder": {
        color: TEXT_SECONDARY,
        opacity: 1,
      },
    },
    "& .MuiInputAdornment-root": {
      marginRight: "6px",
    },
  },
  breadcrumb: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    cursor: "pointer",
    px: 1,
    py: 0.5,
    color: TEXT_SECONDARY,
    borderRadius: "6px",
    width: "fit-content",
    // Rendered as a native <button> for keyboard support — strip the UA chrome
    // so it stays visually identical to the former <div>.
    appearance: "none",
    background: "none",
    border: "none",
    font: "inherit",
    "&:hover": {
      color: BRAND,
      backgroundColor: BRAND_ALPHA_06,
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: BRAND,
      outlineOffset: "2px",
    },
  },

  // ── File list ───────────────────────────────────────────────────────
  listContainer: {
    px: 3,
    py: 1.5,
    minHeight: "360px",
    maxHeight: "420px",
    overflowY: "auto",
    "&::-webkit-scrollbar": { width: "8px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(18, 24, 38, 0.15)",
      borderRadius: "4px",
    },
  },
  fileList: {
    gap: 0.5,
  },
  fileRow: (isSelected: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 1.5,
    py: 1.25,
    borderRadius: "10px",
    cursor: "pointer",
    border: "1px solid",
    borderColor: isSelected ? BRAND : "transparent",
    backgroundColor: isSelected ? BRAND_ALPHA_06 : "transparent",
    transition: "all 140ms ease",
    "&:hover": {
      backgroundColor: isSelected ? BRAND_ALPHA_08 : SURFACE_ALT,
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: BRAND,
      outlineOffset: "2px",
    },
  }),
  fileIconWrap: (isFolder: boolean) => ({
    width: 38,
    height: 38,
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    backgroundColor: isFolder
      ? "rgba(255, 176, 32, 0.14)"
      : "rgba(224, 65, 65, 0.12)",
    color: isFolder ? "#d98a00" : "#d13a3a",
  }),
  fileMeta: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: "14px",
    fontWeight: 500,
    color: TEXT_PRIMARY,
    lineHeight: 1.35,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileType: {
    fontSize: "12px",
    color: TEXT_SECONDARY,
    lineHeight: 1.25,
    mt: "2px",
  },
  chevron: {
    color: TEXT_SECONDARY,
    fontSize: 22,
    flexShrink: 0,
  },
  checkIcon: {
    color: BRAND,
    fontSize: 22,
    flexShrink: 0,
  },
  checkPlaceholder: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: `1.5px solid ${BORDER}`,
    flexShrink: 0,
  },

  // ── Empty state ─────────────────────────────────────────────────────
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 1.25,
    py: 6,
    px: 3,
    textAlign: "center",
    minHeight: "320px",
  },
  emptyIconWrap: {
    width: 96,
    height: 96,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(135deg, ${BRAND_ALPHA_12} 0%, ${BRAND_ALPHA_04} 100%)`,
    color: BRAND,
    mb: 1,
  },
  emptyTitle: {
    fontSize: "15.5px",
    fontWeight: 600,
    color: TEXT_PRIMARY,
  },
  emptySubtitle: {
    fontSize: "13px",
    color: TEXT_SECONDARY,
    maxWidth: "340px",
    lineHeight: 1.5,
  },

  // ── Footer content ──────────────────────────────────────────────────
  selectedChip: {
    maxWidth: "220px",
    backgroundColor: BRAND_ALPHA_10,
    // BRAND (#dd3824) on the pale-orange chip fill is ~4.3:1 — just under AA
    // 1.4.3 (4.5:1). BRAND_DARK clears it while staying in the brand family.
    color: BRAND_DARK,
    fontWeight: 500,
    "& .MuiChip-deleteIcon": {
      color: BRAND_DARK,
      "&:hover": { color: BRAND_DARK },
    },
  },
  // Cancel / Upload take the global `MuiButton` chrome the Modal System's
  // footer expects — no local overrides.
};

export default cloudStoragePickerStyles;
